export interface DecodedWav {
  sampleRate: number;
  channelCount: number;
  samples: Float32Array[];
}

function readAscii(view: DataView, offset: number, length: number): string {
  let value = "";

  for (let index = 0; index < length; index += 1) {
    value += String.fromCharCode(view.getUint8(offset + index));
  }

  return value;
}

export function decodeWav(buffer: ArrayBuffer): DecodedWav {
  const view = new DataView(buffer);

  if (readAscii(view, 0, 4) !== "RIFF" || readAscii(view, 8, 4) !== "WAVE") {
    throw new Error("Unsupported WAV container");
  }

  let format = 0;
  let channelCount = 0;
  let sampleRate = 0;
  let bitsPerSample = 0;
  let dataOffset = 0;
  let dataLength = 0;
  let offset = 12;

  while (offset + 8 <= view.byteLength) {
    const chunkId = readAscii(view, offset, 4);
    const chunkSize = view.getUint32(offset + 4, true);
    const chunkDataOffset = offset + 8;

    if (chunkId === "fmt ") {
      format = view.getUint16(chunkDataOffset, true);
      channelCount = view.getUint16(chunkDataOffset + 2, true);
      sampleRate = view.getUint32(chunkDataOffset + 4, true);
      bitsPerSample = view.getUint16(chunkDataOffset + 14, true);
    } else if (chunkId === "data") {
      dataOffset = chunkDataOffset;
      dataLength = chunkSize;
      break;
    }

    offset = chunkDataOffset + chunkSize + (chunkSize % 2);
  }

  if (!dataOffset || !channelCount || !sampleRate || !bitsPerSample) {
    throw new Error("Incomplete WAV metadata");
  }

  const bytesPerSample = bitsPerSample / 8;

  if (format !== 1 && format !== 3) {
    throw new Error(`Unsupported WAV format: ${format}`);
  }

  if (!Number.isInteger(bytesPerSample) || bytesPerSample < 2) {
    throw new Error(`Unsupported WAV bit depth: ${bitsPerSample}`);
  }

  const frameCount = Math.floor(dataLength / (channelCount * bytesPerSample));
  const samples = Array.from({ length: channelCount }, () => new Float32Array(frameCount));
  let cursor = dataOffset;

  for (let frame = 0; frame < frameCount; frame += 1) {
    for (let channel = 0; channel < channelCount; channel += 1) {
      let value = 0;

      if (format === 3 && bitsPerSample === 32) {
        value = view.getFloat32(cursor, true);
      } else if (bitsPerSample === 16) {
        value = view.getInt16(cursor, true) / 32768;
      } else if (bitsPerSample === 24) {
        const sample =
          view.getUint8(cursor) |
          (view.getUint8(cursor + 1) << 8) |
          (view.getUint8(cursor + 2) << 16);
        value = ((sample << 8) >> 8) / 8388608;
      } else if (bitsPerSample === 32) {
        value = view.getInt32(cursor, true) / 2147483648;
      } else {
        throw new Error(`Unsupported WAV bit depth: ${bitsPerSample}`);
      }

      samples[channel][frame] = Math.max(-1, Math.min(1, value));
      cursor += bytesPerSample;
    }
  }

  return { sampleRate, channelCount, samples };
}

export function mixToMono(decoded: DecodedWav): Float32Array {
  if (decoded.channelCount === 1) {
    return decoded.samples[0];
  }

  const mono = new Float32Array(decoded.samples[0].length);

  for (let frame = 0; frame < mono.length; frame += 1) {
    let sample = 0;

    for (let channel = 0; channel < decoded.channelCount; channel += 1) {
      sample += decoded.samples[channel][frame] ?? 0;
    }

    mono[frame] = sample / decoded.channelCount;
  }

  return mono;
}

export function duplicateMonoToStereo(mono: Float32Array): Float32Array {
  const stereo = new Float32Array(mono.length * 2);
  stereo.set(mono, 0);
  stereo.set(mono, mono.length);
  return stereo;
}
